import GenericEditor, { type GenericEditorProps } from "#root/components/generic-editor";
import { GlyphLoading } from "#root/components/glyphs";
import { useData } from "#root/utility/hooks";
import { mergeIntoResult } from "#root/utility/merge-into-result";
import type { RecordModificationResponse } from "#root/utility/remote-data";
import type { ReactNode } from "react";
import { useCallback } from "react";
import * as z from "zod";

export const StandardCollectionSchema = z.object({
  id: z.union([z.string(), z.number()]),
});

export type CollectionEditorProps<
  ListRowType extends z.ZodType<{ id: string | number }> = typeof StandardCollectionSchema,
  DetailRowType extends ListRowType = ListRowType,
> = GenericEditorProps<ListRowType, DetailRowType> & {
  header?: ReactNode;
  formClass?: string;
};

export default function CollectionEditor<
  ListRowType extends z.ZodType<{ id: string | number }> = typeof StandardCollectionSchema,
  DetailRowType extends ListRowType = ListRowType,
>({ collection, header, children, formClass }: CollectionEditorProps<ListRowType, DetailRowType>) {
  type ListRow = z.output<ListRowType>;
  type DetailRow = z.output<DetailRowType>;

  const [items, setItems] = useData(() => collection.list(), [collection]);

  const handleUpdate = useCallback(
    (data: RecordModificationResponse<DetailRow>) => {
      setItems(mergeIntoResult(items!, data));
    },
    [setItems, items],
  );

  if (!items) {
    return <GlyphLoading/>;
  }

  return (
    <div className="collection-editor">
      {header}
      {items.rows.map((row: ListRow) => (
        <GenericEditor key={row.id} id={row.id} initialData={row} collection={collection} onSave={handleUpdate} onDelete={handleUpdate} className={formClass}>
          {children}
        </GenericEditor>
      ))}
      <GenericEditor key={"new"} initialData={{}} collection={collection} onSave={handleUpdate} className={formClass}>
        {children}
      </GenericEditor>
    </div>
  );
}
